import React, { useEffect, useMemo, useState } from "react";
import { Column } from "../../../interfaces/table.interface";
import TableComponent from "../../../components/table/TableComponent";
import { get } from "../../../redux/ApiHelper";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { formatDateTime } from "../../../utils/date&time.util";

type SlotStatus = "pending" | "approved" | "rejected";
type StatusFilter = "all" | SlotStatus;

type PopulatedUser = {
  _id?: string;
  name?: string;
  email?: string;
};

type PopulatedPeriod = {
  _id?: string;
  teacher?: PopulatedUser;
  parent?: PopulatedUser;
  student?: PopulatedUser;
};

export type SlotIncreaseRequestRow = {
  _id: string;
  byParent?: boolean;
  byTeacher?: boolean;
  noOfClass?: number;
  gcChargePerClass?: number;
  isApproveByParent?: SlotStatus;
  tuitionPeriod?: PopulatedPeriod;
  createdAt?: string;
  updatedAt?: string;
};

const STATUS_FILTERS: { label: string; value: StatusFilter }[] = [
  { label: "All", value: "all" },
  { label: "Pending", value: "pending" },
  { label: "Approved", value: "approved" },
  { label: "Rejected", value: "rejected" },
];

const statusChipClass = (status?: string) => {
  switch (status) {
    case "approved":
      return "bg-green-100 text-green-700";
    case "rejected":
      return "bg-red-100 text-red-700";
    default:
      return "bg-yellow-100 text-yellow-800";
  }
};

const SlotIncreaseRequests = () => {
  const [requests, setRequests] = useState<SlotIncreaseRequestRow[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [loading, setLoading] = useState(false);
  const { signedAdmin } = useSelector((state: RootState) => state.admin);

  useEffect(() => {
    const fetchRequests = async () => {
      setLoading(true);
      try {
        const queryParams = new URLSearchParams();
        queryParams.set("type", String(signedAdmin.type));
        queryParams.set("adminId", String(signedAdmin.id));
        const res = await get(
          `/admin/tuition/slot-increase-requests?${queryParams.toString()}`
        );
        setRequests(res?.slotIncreaseRequests || res?.data?.slotIncreaseRequests || []);
      } catch {
        setRequests([]);
      } finally {
        setLoading(false);
      }
    };

    if (signedAdmin?.id) {
      fetchRequests();
    }
  }, [signedAdmin?.id, signedAdmin?.type]);

  const filteredRequests = useMemo(() => {
    const lowerQuery = searchQuery.trim().toLowerCase();

    return requests.filter((row) => {
      const status = row.isApproveByParent || "pending";
      if (statusFilter !== "all" && status !== statusFilter) {
        return false;
      }

      if (!lowerQuery) {
        return true;
      }

      const teacherName = row.tuitionPeriod?.teacher?.name || "";
      const parentName = row.tuitionPeriod?.parent?.name || "";
      const studentName = row.tuitionPeriod?.student?.name || "";
      const requestedBy = row.byTeacher
        ? "teacher"
        : row.byParent
          ? "parent"
          : "";

      return (
        teacherName.toLowerCase().includes(lowerQuery) ||
        parentName.toLowerCase().includes(lowerQuery) ||
        studentName.toLowerCase().includes(lowerQuery) ||
        status.toLowerCase().includes(lowerQuery) ||
        requestedBy.includes(lowerQuery) ||
        String(row.noOfClass ?? "").includes(lowerQuery) ||
        String(row.gcChargePerClass ?? "").includes(lowerQuery)
      );
    });
  }, [requests, searchQuery, statusFilter]);

  const columns: Column[] = [
    {
      header: "Requested by",
      render: (_value, row: SlotIncreaseRequestRow) =>
        row.byTeacher ? "Teacher" : row.byParent ? "Parent" : "N/A",
    },
    {
      header: "Teacher",
      render: (_value, row: SlotIncreaseRequestRow) =>
        row.tuitionPeriod?.teacher?.name || "N/A",
    },
    {
      header: "Parent",
      render: (_value, row: SlotIncreaseRequestRow) =>
        row.tuitionPeriod?.parent?.name || "N/A",
    },
    {
      header: "Student",
      render: (_value, row: SlotIncreaseRequestRow) =>
        row.tuitionPeriod?.student?.name || "N/A",
    },
    {
      header: "Classes",
      align: "center",
      render: (_value, row: SlotIncreaseRequestRow) => row.noOfClass ?? "N/A",
    },
    {
      header: "GC / class",
      align: "right",
      render: (_value, row: SlotIncreaseRequestRow) =>
        row.gcChargePerClass != null
          ? row.gcChargePerClass.toLocaleString("en-IN")
          : "N/A",
    },
    {
      header: "Total GC",
      align: "right",
      render: (_value, row: SlotIncreaseRequestRow) => {
        if (row.noOfClass == null || row.gcChargePerClass == null) {
          return "N/A";
        }
        return (row.noOfClass * row.gcChargePerClass).toLocaleString("en-IN");
      },
    },
    {
      header: "Status",
      align: "center",
      render: (_value, row: SlotIncreaseRequestRow) => {
        const status = row.isApproveByParent || "pending";
        return (
          <span
            className={`inline-block px-3 py-1 rounded-full text-xs font-semibold capitalize ${statusChipClass(
              status
            )}`}
          >
            {status}
          </span>
        );
      },
    },
    {
      header: "Created",
      render: (_value, row: SlotIncreaseRequestRow) =>
        row.createdAt ? formatDateTime(row.createdAt) : "N/A",
    },
    {
      header: "Updated",
      render: (_value, row: SlotIncreaseRequestRow) =>
        row.updatedAt ? formatDateTime(row.updatedAt) : "N/A",
    },
  ];

  return (
    <div className="table-container">
      <div className="title">Slot Increase Requests</div>

      <div className="flex flex-wrap gap-2 mb-4">
        {STATUS_FILTERS.map((filter) => (
          <button
            key={filter.value}
            type="button"
            onClick={() => setStatusFilter(filter.value)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition ${
              statusFilter === filter.value
                ? "bg-indigo-600 text-white border-indigo-600"
                : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
            }`}
          >
            {filter.label}
          </button>
        ))}
      </div>

      <div className="search-container mb-4">
        <input
          type="text"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-gray-700"
          placeholder="Search by teacher, parent, student, or status"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {loading ? (
        <p className="text-gray-500">Loading slot increase requests...</p>
      ) : (
        <TableComponent columns={columns} data={filteredRequests} />
      )}
    </div>
  );
};

export default SlotIncreaseRequests;
